/** Run-log surfaces for a preflight item's action executions.
 *
 * `PreflightItemRunLogDrawer` — right-hand drawer listing every recorded
 * `Preflight_Action_Run` (already secret-redacted server-side): mode, status,
 * actor, timing, summary, and the step log. Mirrors `SessionDetailDrawer`.
 * Filterable over everything it shows, and each run copies as text — the shape
 * you paste into a ticket. A long log renders its first lines and offers the
 * rest, rather than pouring thousands of lines into the drawer.
 * `PreflightPreviewDialog` — shows a single dry-run result (a preview run).
 */
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ChipProps } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { items } from '@/api/preflight';
import type { PreflightActionRun, PreflightRunStatus } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { userActions } from '../tenantLinks';

/** Log lines shown before the run offers to expand. A sync run's log is a few
 *  lines; an import's is thousands, and the drawer used to render every one. */
const LOG_LINE_CAP = 20;

/** Everything about a run, as text — what a colleague gets pasted into a ticket. */
function runAsText(run: PreflightActionRun): string {
  return [
    `${run.mode} · ${run.status} · ${fmtTs(run.started_ts)}`,
    run.summary,
    run.error_message,
    ...run.log,
  ]
    .filter(Boolean)
    .join('\n');
}

/** Does this run match the drawer's filter? Searches everything shown. */
function matches(run: PreflightActionRun, q: string): boolean {
  if (!q) return true;
  return runAsText(run).toLowerCase().includes(q.toLowerCase());
}

function runStatusColor(status: PreflightRunStatus): ChipProps['color'] {
  switch (status) {
    case 'ok': return 'success';
    case 'error': return 'error';
    case 'running': return 'warning';
    default: return 'default';
  }
}

function fmtTs(ts: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
}

/** One run rendered as a compact block (shared by the drawer + preview dialog). `formatTs`
 *  writes its start time the host surface's way (the browser's locale by default). */
export function RunRow({
  run,
  formatTs = fmtTs,
}: {
  run: PreflightActionRun;
  formatTs?: (ts: string | null) => string;
}) {
  const snackbar = useSnackbar();
  const [showAll, setShowAll] = useState(false);
  const lines = showAll ? run.log : run.log.slice(0, LOG_LINE_CAP);
  const hidden = run.log.length - lines.length;

  const copy = () => {
    void navigator.clipboard
      ?.writeText(runAsText(run))
      .then(() => snackbar.show('Run copied.', 'success'))
      .catch(() => snackbar.show('Could not copy.', 'error'));
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        <Chip size="small" variant="outlined" label={run.mode} />
        <Chip size="small" color={runStatusColor(run.status)} label={run.status} />
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          {formatTs(run.started_ts)}
          {run.duration_ms != null && ` · ${run.duration_ms} ms`}
          {run.actor && (
            <>
              {' · '}
              <ValueActions actions={userActions(run.actor)}>
                {run.actor.name || run.actor.email}
              </ValueActions>
            </>
          )}
        </Typography>
        <Tooltip title="Copy this run">
          <IconButton size="small" onClick={copy} aria-label="Copy run">
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      {run.summary && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {run.summary}
        </Typography>
      )}
      {/* The error once: a summary that already says it gets no alert repeating it. */}
      {run.status === 'error' &&
        run.error_message &&
        run.error_message.trim() !== (run.summary ?? '').trim() && (
        <Alert severity="error" sx={{ mt: 0.5 }}>
          {run.error_message}
        </Alert>
      )}
      {run.log.length > 0 && (
        <>
          <Box
            component="pre"
            sx={{
              mt: 0.5,
              p: 1,
              m: 0,
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              overflowX: 'auto',
            }}
          >
            {lines.join('\n')}
          </Box>
          {hidden > 0 && (
            <Link
              component="button"
              type="button"
              variant="caption"
              onClick={() => setShowAll(true)}
              sx={{ mt: 0.25 }}
            >
              Show {hidden} more line{hidden === 1 ? '' : 's'}
            </Link>
          )}
        </>
      )}
    </Box>
  );
}

interface DrawerProps {
  topId: number;
  itemId: number | null;
  title?: string;
  open: boolean;
  onClose: () => void;
}

export function PreflightItemRunLogDrawer({ topId, itemId, title, open, onClose }: DrawerProps) {
  const [search, setSearch] = useState('');
  const query = useQuery({
    queryKey: ['preflight', 'item-runs', topId, itemId],
    queryFn: () => items.listRuns(topId, itemId as number),
    enabled: open && itemId != null,
  });
  const runs = query.data ?? [];
  const shown = runs.filter((r) => matches(r, search));

  return (
    <Drawer anchor="right" open={open} onClose={onClose} sx={{ zIndex: (t) => t.zIndex.modal }}>
      <Box sx={{ width: 'min(92vw, 640px)', p: 2 }} role="presentation">
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Run log
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close run log">
            <CloseIcon />
          </IconButton>
        </Stack>
        {title && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {title}
          </Typography>
        )}

        {runs.length > 1 && (
          <TextField
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter runs — summary, error or log text…"
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}

        {query.isLoading && <CircularProgress />}
        {query.isError && <Alert severity="error">Couldn’t load the run log.</Alert>}
        {query.data && runs.length === 0 && <Alert severity="info">No runs recorded yet.</Alert>}
        {runs.length > 0 && shown.length === 0 && (
          <Alert severity="info">No run matches “{search}”.</Alert>
        )}

        <Stack spacing={1.5} divider={<Divider flexItem />}>
          {shown.map((run) => (
            <RunRow key={run.id} run={run} />
          ))}
        </Stack>
      </Box>
    </Drawer>
  );
}

export function PreflightPreviewDialog({
  run,
  onClose,
}: {
  run: PreflightActionRun | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={run != null} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Preview — dry run (no changes made)</DialogTitle>
      <DialogContent dividers>{run && <RunRow run={run} />}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
